"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.samePageConnectorEnds = exports.isFractionalIndex = exports.PageStructureEntryFormatError = exports.PAGE_MINDMAP_KIND = exports.PAGE_SURFACE_FLAVOUR = exports.PAGE_CONNECTOR_KIND = exports.PAGE_STRUCTURE_MAP_KEY = void 0;
exports.isPageElementKind = isPageElementKind;
exports.encodePageStructureEntry = encodePageStructureEntry;
exports.normalizePageStructureEntry = normalizePageStructureEntry;
exports.decodePageStructureEntry = decodePageStructureEntry;
exports.validatePageStructureEntry = validatePageStructureEntry;
const database_document_scheme_1 = require("./generated/database-document-scheme");
/** Map of the page document that holds one structure entry per block and canvas element. */
exports.PAGE_STRUCTURE_MAP_KEY = 'pageStructure';
/** Element kind of a connector: its entry names the ends it is attached to. */
exports.PAGE_CONNECTOR_KIND = 'connector';
/** Flavour of the block that holds the canvas elements of a page. */
exports.PAGE_SURFACE_FLAVOUR = 'affine:surface';
/** Element kind of a mindmap and its indivisible shape unit. */
exports.PAGE_MINDMAP_KIND = 'mindmap';
/**
 * Block flavours are namespaced (`affine:note`), canvas element types are
 * not (`shape`): the kind of an entry tells which of the two it is.
 */
function isPageElementKind(kind) {
    return !kind.includes(':');
}
/** A stored value is no structure entry. */
class PageStructureEntryFormatError extends Error {
    constructor() {
        super(...arguments);
        this.name = 'PageStructureEntryFormatError';
    }
}
exports.PageStructureEntryFormatError = PageStructureEntryFormatError;
// Fractional indexes of BlockSuite and of canvas elements are base62 keys.
const FRACTIONAL_INDEX = /^[0-9A-Za-z]+$/;
const isFractionalIndex = (value) => typeof value === 'string' && FRACTIONAL_INDEX.test(value);
exports.isFractionalIndex = isFractionalIndex;
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
const isConnectorEnd = (value) => value === null || (typeof value === 'string' && value.length > 0);
const isConnectorEnds = (value) => Array.isArray(value) &&
    value.length === 2 &&
    isConnectorEnd(value[0]) &&
    isConnectorEnd(value[1]);
const samePageConnectorEnds = (a, b) => a === b || (!!a && !!b && a[0] === b[0] && a[1] === b[1]);
exports.samePageConnectorEnds = samePageConnectorEnds;
/** The stored value: optional fields are left out, never stored as null. */
function encodePageStructureEntry(entry) {
    return {
        k: entry.k,
        p: entry.p,
        o: entry.o,
        ...(entry.g === undefined ? {} : { g: entry.g }),
        ...(entry.pi === undefined ? {} : { pi: entry.pi }),
        ...(entry.l === undefined ? {} : { l: entry.l }),
        ...(entry.d === undefined ? {} : { d: entry.d }),
        ...(entry.c === undefined ? {} : { c: [entry.c[0], entry.c[1]] }),
    };
}
/**
 * Canonical form of a stored value, or null when it is no entry. Values that
 * crossed the Rust/WASM CRDT codec lose their key order, so entries are
 * compared in this form only.
 */
function normalizePageStructureEntry(value, partId) {
    if (!isRecord(value))
        return null;
    const { k, p, o, g, pi, l, d, c } = value;
    if (typeof k !== 'string' ||
        (p !== null && typeof p !== 'string') ||
        typeof o !== 'string' ||
        typeof partId !== 'string' ||
        !partId ||
        (g !== undefined && typeof g !== 'string') ||
        (pi !== undefined && typeof pi !== 'string') ||
        (l !== undefined && l !== null && !(0, exports.isFractionalIndex)(l)) ||
        (d !== undefined && (typeof d !== 'string' || !/^[0-9]+$/.test(d))) ||
        (c !== undefined && !isConnectorEnds(c)) ||
        Object.keys(value).some(key => !['k', 'p', 'o', 'g', 'pi', 'l', 'd', 'c'].includes(key))) {
        return null;
    }
    return {
        k,
        p,
        o,
        s: partId,
        ...(g === undefined ? {} : { g }),
        ...(pi === undefined ? {} : { pi }),
        ...(l === undefined ? {} : { l }),
        ...(d === undefined ? {} : { d }),
        ...(c === undefined ? {} : { c: [c[0], c[1]] }),
    };
}
function decodePageStructureEntry(value, partId) {
    const entry = normalizePageStructureEntry(value, partId);
    if (!entry) {
        throw new PageStructureEntryFormatError('Stored value is no page structure entry');
    }
    return entry;
}
/** Every rule the entry of block or element `id` breaks, in a fixed order. */
function validatePageStructureEntry(id, entry) {
    const issues = [];
    if (!entry.k)
        issues.push('empty-kind');
    if (entry.p === id)
        issues.push('self-parent');
    if (!entry.s || entry.s.includes(database_document_scheme_1.DATABASE_DOC_ID_SEPARATOR))
        issues.push('invalid-part-id');
    if (!(0, exports.isFractionalIndex)(entry.o))
        issues.push('invalid-order');
    if (entry.l !== undefined &&
        ((entry.l !== null && !(0, exports.isFractionalIndex)(entry.l)) ||
            isPageElementKind(entry.k)))
        issues.push('invalid-layer');
    if ((entry.k === exports.PAGE_CONNECTOR_KIND) !== (entry.c !== undefined))
        issues.push('invalid-connector-ends');
    return issues;
}
